<script setup lang="ts">
import { computed } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import { useLegalPageSeo } from '@/composables/useLegalPageSeo';
import type { LegalDocument } from '@/composables/useLegalPageSeo';
import { useTranslations } from '@/composables/useTranslations';

const props = defineProps<{
    document: LegalDocument;
}>();

const { t, tList } = useTranslations();

const seo = useLegalPageSeo(props.document, t);

const title = computed(() =>
    t(`legal.${props.document}.title`, props.document),
);

const paragraphs = computed(() =>
    tList(`legal.${props.document}.paragraphs`, []),
);

const lastUpdated = computed(() =>
    t(`legal.${props.document}.last_updated`, ''),
);

const jsonLdHeadKey = computed(() => `ld-json-legal-${props.document}`);
</script>

<template>
    <ToolPageHead v-bind="seo" :json-ld-head-key="jsonLdHeadKey" />

    <article
        class="mx-auto w-full max-w-3xl space-y-8 px-4 pt-12 pb-20 sm:px-6 sm:pt-16 sm:pb-24 md:pt-20 md:pb-28"
        data-testid="legal-document"
        :data-document="document"
    >
        <header class="space-y-2 border-b border-border/70 pb-8">
            <h1
                class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
            >
                {{ title }}
            </h1>
            <p
                v-if="lastUpdated"
                class="text-sm font-medium text-muted-foreground"
            >
                {{ lastUpdated }}
            </p>
        </header>
        <div
            class="space-y-5 text-sm leading-relaxed text-muted-foreground sm:space-y-6 sm:text-base"
        >
            <p v-for="(paragraph, index) in paragraphs" :key="index">
                {{ paragraph }}
            </p>
        </div>
    </article>
</template>
